import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Copy, ExternalLink, CheckCircle, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Pixel } from '@/types/pixel';
import { PixelActionMenu } from './PixelActionMenu';
import { PixelStatusBadge } from './PixelStatusBadge';
import { PixelHealthBadge } from './PixelHealthBadge';
import { toast } from '@/hooks/use-toast';

interface PixelCardMobileProps {
  pixel: Pixel;
  onRemove: (pixel: Pixel) => void;
}

export function PixelCardMobile({ pixel, onRemove }: PixelCardMobileProps) {
  useLanguage();
  const [copied, setCopied] = useState(false);

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(pixel.token);
      setCopied(true);
      toast({
        title: translateInterfaceText("Copiado!"),
        description: translateInterfaceText("Token copiado para a área de transferência."),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: translateInterfaceText("Erro ao copiar"),
        description: translateInterfaceText("Não foi possível copiar o token."),
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy", { locale: getInterfaceDateLocale() });
    } catch {
      return '-';
    }
  };

  return (
    <div className="border border-[#E5E7EB] rounded-xl bg-white p-4 transition-shadow hover:shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <Radio className="w-5 h-5 text-[#437DFF]" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-[#111] capitalize">
              {pixel.platform.replace('-', ' ')} {" "}{translateInterfaceText("Pixel")}</h4>
          </div>
        </div>
        <PixelActionMenu
          pixel={pixel}
          onRemove={onRemove}
        />
      </div>

      {/* Token */}
      <div className="mb-3">
        <div className="text-xs text-[#64748B] mb-1">{translateInterfaceText("Token:")}</div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#111] font-mono flex-1">
            ••••••••••••
          </span>
          <button
            onClick={copyToken}
            className="text-[#64748B] hover:text-[#437DFF] transition-colors p-1.5 rounded hover:bg-blue-50"
            aria-label={translateInterfaceText("Copiar token")}
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* URL */}
      <div className="mb-3">
        <div className="text-xs text-[#64748B] mb-1">{translateInterfaceText("URL vinculada:")}</div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#111] truncate flex-1">{pixel.siteUrl}</span>
          <a
            href={pixel.siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#64748B] hover:text-[#437DFF] transition-colors p-1.5 rounded hover:bg-blue-50"
            aria-label={translateInterfaceText("Abrir URL")}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Video */}
      <div className="mb-3">
        <div className="text-xs text-[#64748B] mb-1">{pixel.flowId ? translateInterfaceText("Funil:") : translateInterfaceText("Vídeo:")}</div>
        <div className="flex items-center gap-3">
          {pixel.videoThumbnail ? (
            <img
              src={pixel.videoThumbnail}
              alt={pixel.videoName}
              className="w-12 h-8 rounded object-cover"
            />
          ) : (
            <div className="w-12 h-8 rounded bg-[#F1F5F9] flex items-center justify-center">
              <span className="text-[8px] text-[#64748B]">{translateInterfaceText("VSL")}</span>
            </div>
          )}
          <div>
            <span className="text-sm text-[#111]">{pixel.videoName}</span>
            <span className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide ml-2">
              {pixel.flowId ? translateInterfaceText("VSL Dinâmica") : translateInterfaceText("VSL")}
            </span>
          </div>
        </div>
      </div>

      {/* Criado em */}
      <div className="mb-4">
        <div className="text-xs text-[#64748B] mb-1">{translateInterfaceText("Criado em:")}</div>
        <span className="text-sm text-[#111]">{formatDate(pixel.createdAt)}</span>
      </div>

      {/* Eventos enviados */}
      <div className="mb-4">
        <div className="text-xs text-[#64748B] mb-1">{translateInterfaceText("Eventos enviados:")}</div>
        <span className="text-sm text-[#111]">{pixel.eventsSent || '—'}</span>
      </div>

      {/* Status */}
      <div>
        <div className="text-xs text-[#64748B] mb-2">{translateInterfaceText("Status:")}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <PixelStatusBadge status={pixel.status} />
          {pixel.active && <PixelHealthBadge health={pixel.health} />}
        </div>
      </div>
    </div>
  );
}
